import { DataGrid } from "@mui/x-data-grid";
import * as React from "react";
import { useSelector } from "react-redux";
import { useDemoData } from "@mui/x-data-grid-generator";
import store from "../../../redux/Store";
import { userActions } from "../../../redux/reducers/UserReducer";
import { PARKERS_PROPS, PARKING_STATUSES, SUBSCRIPTION_STATUSES, SEARCH_PROPS, USER_TYPES, TABLES, HISTORY_PROPS, CAMPAIGN_PROPS } from "../../../enums/Enums";
import { flattenObj, isSameObj } from "../../../utils/CommonUtils";
import { CustomToolbar } from "../ToolsBar/CustomToolbar/CustomToolbar";
import classes from "./DataTable.module.css";

const DataTable = (props) => {
  const { data } = useDemoData({
    dataSet: 'Commodity',
    rowLength: 100
  });

  const [currentScope, setCurrentScope] = React.useState(null);
  const [paginationModel, setPaginationModel] = React.useState({page: 0, pageSize: 100});
  const [searchText, setSearchText] = React.useState("");
  const [rows, setRows] = React.useState([]);
  const [initialFilteredRows, setInitialFilteredRows] = React.useState([]);
  const [sortModel, setSortModel] = React.useState([]);

  const selectedSites = useSelector((state) => {
    return state.user?.selectedSites;
  });

  const initialFilter = {};

  initialFilter[PARKERS_PROPS.APP] = { value: false };
  initialFilter[PARKERS_PROPS.ELECTRIC] = { value: false };
  initialFilter[PARKERS_PROPS.SITES] = { value: [] };
  initialFilter[PARKERS_PROPS.USER_TYPES] = { value: [] };
  initialFilter[PARKERS_PROPS.PARKING] = { value: [] };
  initialFilter[PARKERS_PROPS.USER_ADDITIONAL_DATA] = { value: [] };
  initialFilter[CAMPAIGN_PROPS.CAMPAIGN_TYPE] = { value: [] };
  initialFilter[HISTORY_PROPS.VALIDATION_TYPE] = { value: [] };
  initialFilter[HISTORY_PROPS.CHARGE_ACCOUNT] = { value: '' }; 

  const [selectedFilter, setSelectedFilter] = React.useState(
    () => initialFilter
  );

  // represents the dynamic founded date column key prop name on current table
  const [foundedDateKey, setFoundedDateKey] = React.useState(null);

  // Checks if filter is active, 
  // When filter is active currentRows to set will be as the selected initial filter results
  const isFilterActive = !isSameObj(initialFilter, selectedFilter);
  const currentRows = isFilterActive ? initialFilteredRows : props.rows;

  // when selecting a different scope clear search text, reset table's pagination model, and close user info window
  React.useEffect(() => {
    setRows(props.rows);
    if (props?.selectedScope?.id !== currentScope?.id) {
      setCurrentScope(props.selectedScope);
      setSearchText("");
      setSelectedFilter(initialFilter);
      setInitialFilteredRows([]);
      setPaginationModel({page: 0, pageSize: 100});
      store.dispatch(userActions.setShowUserInfoWindow(false));
    } else if (searchText) {
      requestSearch(searchText);
    }
  }, [props.selectedScope, props.rows]);

  React.useEffect(() => {
    // when selected table is public parkers, set auto-sort time column to descending
    if (props?.tableId === TABLES.GUESTS) {
      setSortModel([{ field: PARKERS_PROPS.TIME, sort: 'desc' }]);
    }
    // else if (props?.tableId === TABLES.HISTORY || props?.tableId === TABLES.BILLING_HISTORY) {
    //   setSortModel([{ field: HISTORY_PROPS.DATE, sort: 'desc' }]);
    // }
    else if (foundedDateKey !== null) {
      setSortModel([{ field: foundedDateKey, sort: 'desc' }]);
    }
    else {
      setSortModel([]);
    }
  }, [props?.tableId, foundedDateKey]);

  React.useEffect(() => {
    // For dynamic date prop name filter, any date column in any table that is identified 
    // will be set dynamically along initialFilter
    // identify date key by given row data
    if (props?.rows?.length > 0) {
      Object.entries(props?.rows?.[0])?.forEach(entry => {
        // check if its a date string
        if (typeof entry[1] === 'string' && entry[1]?.includes('/')) {
          const foundedDate = !isNaN(new Date(entry[1]));
          if (foundedDate) {
            const foundedDateKey = entry[0];
            setSelectedFilter({ ...selectedFilter, [foundedDateKey] : {value : []}});
            setFoundedDateKey(foundedDateKey);
          }
        }
      });
    }
  }, [props?.rows]);

  const handleRowSelection = (data) => {
    props.onRowClick(data.row);
  };

  const requestSearch = (searchValue) => {
    const filteredRows = currentRows.filter((row) => {
      const flattenRows = flattenObj(row?.utronUser ? { ...row?.utronUser, ...row } : row);
      return Object.values(SEARCH_PROPS).some((field) => {
        if (field === SEARCH_PROPS.NOTES) {
          return isNotesFits(searchValue, field, flattenRows);
        }
        if (field === SEARCH_PROPS.LICENSE_PLATE) {
          return isLicensePlateFits(searchValue, field, flattenRows);
        }
        if (field === SEARCH_PROPS.PHONE_NUMBER) {
          return isPhoneNumberFits(searchValue, field, flattenRows);
        }
        if (field === SEARCH_PROPS.CAR_USER_TOKENS) {
          return isCarUserTokensFits(searchValue, field, flattenRows);
        }
        if (field === SEARCH_PROPS.FIRST_NAME) {
          return flattenRows[SEARCH_PROPS.FIRST_NAME] && flattenRows[SEARCH_PROPS.FIRST_NAME].trim().concat(' ', flattenRows[SEARCH_PROPS.LAST_NAME]).toLowerCase().includes((searchValue).toLowerCase());
        }
        // allocation area - handles parker name 
        if (field === SEARCH_PROPS.PARKER_NAME) {
          return flattenRows[SEARCH_PROPS.PARKER_NAME] && flattenRows[SEARCH_PROPS.PARKER_NAME].trim().concat(' ', flattenRows[SEARCH_PROPS.PARKER_LAST_NAME]).toLowerCase().includes((searchValue).toLowerCase());
        }
        if (field === SEARCH_PROPS.CAR_DETAILS) {
          return flattenRows[SEARCH_PROPS.CAR_DETAILS] && flattenRows[SEARCH_PROPS.CAR_DETAILS].trim().concat(' ', flattenRows[SEARCH_PROPS.CAR_LICENSE_PLATE]).toLowerCase().includes((searchValue).toLowerCase());
        }
        return (
          flattenRows[field] && flattenRows[field].toString().toLowerCase().includes((searchValue).toLowerCase()) /*searchRegex.test(flattenRows[field].toString())*/
        );
      });
    });

    if (props?.onSearchedRowsChange) {
      props.onSearchedRowsChange(filteredRows);
    }

    setRows(filteredRows);
  };

  const isNotesFits = (searchValue, field, flattenRows) => {
    return flattenRows[field]?.length > 0 && flattenRows[field][0]?.notes?.replaceAll('\n', ' ').toLowerCase().includes((searchValue).toLowerCase());
  };

  const isLicensePlateFits = (searchValue, field, flattenRows) => {
    return flattenRows[field]?.some(car => car?.licensePlate?.toLowerCase().includes((searchValue).toLowerCase()));
  };

  const isPhoneNumberFits = (searchValue, field, flattenRows) => {
    return flattenRows[field] && (`+${flattenRows[field]}`.includes((searchValue)) || flattenRows[field].includes((searchValue)));
  };

  const isCarUserTokensFits = (searchValue, field, flattenRows) => {
    return flattenRows[field]?.some(cut => cut?.token?.value.includes((searchValue).toLowerCase()) || cut?.token?.displayValue.toLowerCase().includes((searchValue).toLowerCase()));
  };

  const isSiteFits = (filterValue, field, flattenRow) => {
    const sitesIds = flattenRow[field]?.map((site) => site.siteId);
    return filterValue[field].value.some((filetrValue) =>
      sitesIds?.includes(filetrValue));
  };

  const isParkingStatusFits = (filterValue, field, flattenRow) => {
    let isFilterd = false;
    const parkingStatus = flattenRow[field];
    if (filterValue[field].value?.[0] === PARKING_STATUSES.ACTIVE) {
      isFilterd = parkingStatus === PARKING_STATUSES.IN || parkingStatus === PARKING_STATUSES.OUT   
    } else if (parkingStatus === PARKING_STATUSES.INACTIVE) {
      isFilterd = filterValue[field].value.includes(PARKING_STATUSES.INACTIVE);
    } else if (parkingStatus === PARKING_STATUSES.IN) {
      isFilterd = filterValue[field].value.includes(PARKING_STATUSES.IN);
    } else if (parkingStatus === PARKING_STATUSES.OUT) {
      isFilterd = filterValue[field].value.includes(PARKING_STATUSES.OUT);
    }

    return isFilterd;
  };

  const isSubscriptionStatusFits = (filterValue, field, flattenRow) => {
    let isFilterd = false;
    const subscriptionStatus = flattenRow[field]?.[0]?.isMessagingEnabled ? SUBSCRIPTION_STATUSES.SUBSCRIBED : SUBSCRIPTION_STATUSES.UNSUBSCRIBED;
    if (subscriptionStatus === SUBSCRIPTION_STATUSES.SUBSCRIBED) {
      isFilterd = filterValue[field].value.includes(
        SUBSCRIPTION_STATUSES.SUBSCRIBED
      );
    }
    else if (subscriptionStatus === SUBSCRIPTION_STATUSES.UNSUBSCRIBED) {
      isFilterd = filterValue[field].value.includes(
        SUBSCRIPTION_STATUSES.UNSUBSCRIBED
      );
    }
    
    return isFilterd;
  };

  const isAppFits = (filterValue, field, flattenRow) => {
    return filterValue[field].value && flattenRow[field];
  };

  const isUserTypeFits = (filterValue, field, flattenRow) => {
    return filterValue[field].value.includes(
      flattenRow[field].find(
        (type) => selectedSites.some(site => type.userType.siteId === site.Id || (type.userType.siteId === null && type.userTypeId === USER_TYPES.TRANSIENT))
      )?.userTypeId
    )
  };

  const isDateFits = (filterValue, field, flattenRow) => {
    // get the selected dates values & ignore date's hours time on comparison to find the matches
    let startDate = filterValue[field].value[0]?.setHours(0, 0, 0, 0);
    let endDate = filterValue[field].value[1]?.setHours(0, 0, 0, 0);
    let currentRowDate = new Date(flattenRow[field])?.setHours(0, 0, 0, 0);

    // check if currentRowDate is between the selected dates
    return startDate <= currentRowDate && currentRowDate <= endDate;
  };

  const requestFilter = (filterValue) => {
    setSelectedFilter(filterValue);
    const filedsToFilter = Object.keys(selectedFilter).filter((field) => {
      if (Array.isArray(filterValue[field].value))
        return filterValue[field].value.length !== 0;
      else return filterValue[field].value;
    });

    const filteredRows = props.rows.filter((row) => {
      const flattenRows = flattenObj(row);
      let rowFits = true;
      if (filedsToFilter.length) {
        filedsToFilter.forEach((field) => {
          switch (field) {
            case PARKERS_PROPS.SITES:
              rowFits = rowFits && isSiteFits(filterValue, field, flattenRows);
              break;
            case PARKERS_PROPS.PARKING:
              rowFits = rowFits && isParkingStatusFits(filterValue, field, flattenRows);
              break;
            case PARKERS_PROPS.USER_ADDITIONAL_DATA:
              rowFits = rowFits && isSubscriptionStatusFits(filterValue, field, flattenRows);
              break;
            case PARKERS_PROPS.APP:
              rowFits = rowFits && isAppFits(filterValue, field, flattenRows);
              break;
            case PARKERS_PROPS.USER_TYPES:
              rowFits = rowFits && isUserTypeFits(filterValue, field, flattenRows);
              break;
            case foundedDateKey:
              rowFits = rowFits && isDateFits(filterValue, field, flattenRows);
              break;
            default:
              rowFits = rowFits && filterValue[field].value.includes(flattenRows[field]);
          }
        })
      }

      return rowFits;
    });

    if (props?.onFilteredRowsChange) {
      props.onFilteredRowsChange(filteredRows);
    }

    setRows(filteredRows);
    setInitialFilteredRows(filteredRows);
  };
  // const requestFilter = (filterValue) => {
  //   setSelectedFilter(filterValue);

  //   const filteredRows = props.rows.filter((row) => {
  //     const flattenRows = flattenObj(row);
  //     const filedsToFilter = Object.keys(selectedFilter).filter((field) => {
  //       if (Array.isArray(filterValue[field].value))
  //         return filterValue[field].value.length !== 0;
  //       else return filterValue[field].value;
  //     });
  //     return (
  //       !filedsToFilter.length ||
  //       filedsToFilter.some((field) => {
  //         switch (field) {
  //           case PARKERS_PROPS.SITES:
  //             const sitesIds = flattenRows[field].map((site) => site.siteId);
  //             return filterValue[field].value.some((filetrValue) =>
  //               sitesIds.includes(filetrValue)
  //             );
  //           case PARKERS_PROPS.PARKING:
  //             let isFilterd = false;
  //             const parkingStatus = flattenRows[field];
  //             if (parkingStatus === PARKING_STATUSES.INACTIVE) {
  //               isFilterd = filterValue[field].value.includes(
  //                 PARKING_STATUSES.INACTIVE
  //               );
  //             } else if (parkingStatus === PARKING_STATUSES.IN) {
  //               isFilterd = filterValue[field].value.includes(
  //                 PARKING_STATUSES.IN
  //               );
  //             } else if (parkingStatus === PARKING_STATUSES.OUT){
  //               isFilterd = filterValue[field].value.includes(
  //                 PARKING_STATUSES.OUT
  //               );
  //             }
  //             return isFilterd;
  //           case PARKERS_PROPS.USER_TYPES:
  //             return filterValue[field].value.includes(
  //               flattenRows[field].find(
  //                 (type) =>  selectedSites.some(site => type.userType.siteId === site.Id)
  //               )?.userTypeId
  //             );
  //           default:
  //             return filterValue[field].value === flattenRows[field];
  //         }
  //       })
  //     );
  //   });
  //   setRows(filteredRows);
  // };

  const getRowClass = (params) => {
    // check to set the row css to gray style according to the selected table
    // and its conditions
    let isDisableNeeded = false;
    switch (props?.tableId) {
      case TABLES.PARKERS:
        // on Permanent table - checks if all userSites is inactive
        isDisableNeeded = params.row?.utronUser?.userSites?.every(us => !us.isActive);
        break;
      case TABLES.GMT:
        // on GMT table - check if all roles is 'Inactive'
        isDisableNeeded = params.row?.userScopes?.every(us => us?.role?.name === 'Inactive');
        break;
      case TABLES.OCCUPIED:
        // on Allocations section - Occupied table - check if allocation is inactive
        isDisableNeeded = !params.row?.isActive;
        break;
      case TABLES.BILLING_HISTORY:
        // on Billing section - Validation History tab - check if charge account is deleted
        isDisableNeeded = params.row?.chargeAccount?.user?.utronUser?.deleted;
        break;

      default:
        break;
    }


    if (isDisableNeeded) {
      return classes.disableRow;
    } else {
      return classes.row;
    }
  };

  return (
    <div className={classes.dataTable}>
      <DataGrid
        classes={{
          root: classes.dataTableRoot,
          columnHeaders: classes.columnHeaders,
          cell: classes.cell
        }}
        rows={rows}
        columns={props.columns}
        initialState={{
          ...data.initialState,
          pagination: { paginationModel: paginationModel}
        }}
        paginationModel={paginationModel}
        onPaginationModelChange={(model) => setPaginationModel(model)}
        pageSizeOptions={[10, 25, 100]}
        rowHeight={100}
        getRowId={props.getRowId}
        disableColumnMenu
        onRowClick={handleRowSelection}
        getRowClassName={getRowClass}
        sortModel={sortModel}
        onSortModelChange={(model) => setSortModel(model)}
        slots={{ toolbar: CustomToolbar }}
        slotProps={{
          toolbar: {
            isTableModal: props?.isTableModal,
            toolBarTitle: props?.toolBarTitle,
            toolBarSecondaryTitle: props?.toolBarSecondaryTitle,
            tableId: props?.tableId,
            handleClose: props?.handleClose,
            quickDownload: {
              value: rows,
              columns: props.columns,
              tableId: props.tableId,
              onChange: (filterValue) => requestFilter(filterValue),
              clearSearch: () => requestFilter(initialFilter),
            },
            quickSearch: {
              value: searchText,
              onChange: (event) => {
                // When clearing the search field manually, set back initial rows
                if (event.target.value === "") {
                  setRows(currentRows);
                }
                else {
                  // Start search
                  requestSearch(event.target.value)
                }

                // Always two way bind the entered value to the state for visually ui change
                setSearchText(event.target.value);
              },
              clearSearch: () => {
                // reset search field, set back initial rows
                setSearchText("");
                setRows(currentRows);
              },
            },
            quickFilter: {
              value: selectedFilter,
              tableId: props.tableId,
              minDate: props?.minDate,
              maxDate: props?.maxDate,
              foundedDateKey: foundedDateKey,
              onChange: (filterValue) => requestFilter(filterValue),
              clearSearch: () => {
                // If there is a date column founded on table, also reset with initialFilter
                requestFilter(foundedDateKey !== null ? {...initialFilter, [foundedDateKey] : {value : []}} : initialFilter);
                setInitialFilteredRows([]);
              },
            }
          },
        }}
      />
    </div>
  );
};

export default DataTable;
